<template>
	<div class="country-item-list">
		<Collapse
			v-for="(region, index) in regions"
			:key="region.RegionGroupID"
			:open="index === 0"
			class="country-item-list__collapse"
			:data-region="region.RegionGroupName"
			:keep-alive="false"
		>
			<template #header="{ isOpen }">
				<CountryItemListHeader
					:region="region"
					:is-open="isOpen"
					:best-prices="bestPrices"
					:get-regions-count="getRegionsCount"
					:collapse-hint-text="collapseHintText"
					:format-price="formatPrice"
				/>
			</template>
			<template #default>
				<CountryItemListContent
					:region="region"
					:currency="currency"
					:hotel-summary="hotelSummary"
					:best-prices="bestPrices"
				/>
			</template>
		</Collapse>
	</div>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import Collapse from '@lmt-rpb/Collapse/Collapse.vue';
import CountryItemListContent from '@lmt-rpb/CountryItemListContent/CountryItemListContent.vue';
import CountryItemListHeader from '@lmt-rpb/CountryItemListHeader/CountryItemListHeader.vue';
import { getLocaleString } from '@utils/environmentUtils';
import { formatPrice, getTotalPrice } from '@/js/utils/priceUtils';
import type { BestOfferForRegion } from '@/interfaces/api/v1-best-offer-for-region';

import { type RegionItem } from '@/interfaces/region-item';

interface Props {
	items: BestOfferForRegion[];
}

const props = defineProps<Props>();
const currency = computed<string>(() => getLocaleString('currency'));

const regions = computed<RegionItem[]>(() => props.items.map((region) => ({ ...region, code: region.RegionGroupName })));

const bestPrices = computed<Record<string, number>>(() =>
	props.items.reduce((acc, { RegionGroupName, Regions }, index) => {
		const key = RegionGroupName || index;
		const bestPrice = Regions.map(({ Pricing }) => getTotalPrice(Pricing))
			.sort((a, b) => a - b)
			.shift();
		return { ...acc, [key]: bestPrice };
	}, {})
);

const hotelSummary = computed<Record<string, number>>(() =>
	props.items.reduce((acc, { RegionGroupName, Regions }, index) => {
		const key = RegionGroupName || index;
		const count = Regions.reduce((sum, { HotelCount }) => sum + HotelCount, 0);
		return { ...acc, [key]: count };
	}, {})
);

const getRegionsCount = (regionItems: RegionItem[]): string => {
	if (regionItems && regionItems.length > 0) {
		return `(${regionItems.length})`;
	}
	return '';
};

const collapseHintText = (regionItems: RegionItem[]): string => {
	const regionNames = [];
	for (let i = 0; i < 5; i++) {
		if (!regionItems[i]) {
			break;
		}
		regionNames.push(regionItems[i].Name);
	}
	return regionNames.join(', ');
};
</script>

<style lang="scss" scoped>
.country-item-list {
	&__collapse {
		margin-bottom: 1rem;

		:deep(.collapse__header) {
			padding: 2.7rem 1rem;
			margin-bottom: 0.8rem;
			background-color: $header-bar-bg;
			border-radius: $border-radius-large;
			cursor: pointer;
			align-items: center;
			justify-content: space-between;
			box-shadow: 0.3rem 0.6rem 1.4rem 0 #66666620;
			position: relative;
		}

		@media (min-width: $breakpoint-small) {
			:deep(.collapse__header) {
				padding: 1.7rem 1.6rem;
			}
		}

		:deep(.collapse.is-open) {
			color: $color-vivid;
		}

		:deep(.collapse__icon) {
			font-size: 2rem;
			color: $color-primary;
			margin-top: 0.2rem;
		}
	}
}
</style>
